import { memo, useId, useState } from 'react';
import { useCopy } from '../lib/copy';
import {
  contactFields,
  contactKind,
  contactProblem,
  handoverSubjectText,
} from '../lib/handoverContact';

// The web handover form, as Estela's message (docs/plans/2026-10-11-web-handover-form,
// Task 3, the light card Alex chose on 2026-10-11). Nothing opens a case until the
// visitor sends it, chooses to wait, or chooses WhatsApp; ignoring it costs nothing.
//
// `form` is the turn's `handover_form`: { subject, whatsapp, email } to start from, and
// `whatsapp_country_code` ("55"), what a number typed without + is read with, or null when
// the tenant has none and + is required (Task 10). A card saved before Task 10 has no key
// and shows no hint; the API reads the number the same way regardless.
// `onSubmit(fields, sent)` resolves to `{ ok: true, sent }` (what the card shows, with the
// number as the API saved it) or `{ error }` (the server's code).

const WHATSAPP_GREEN = '#1fa855';

// The calm line under the field, by the API's own code. Literal calls, so the
// copy test sees every string.
function problemText(t, code) {
  switch (code) {
    case '':
      return '';
    case 'invalid_email':
      return t('Check the e-mail');
    case 'invalid_whatsapp':
      return t('Check the number, with the area code');
    case 'contact_required':
      return t('Leave a WhatsApp or e-mail, or choose to wait here.');
    default:
      return t('Could not send it now. Please try again.');
  }
}

function WhatsAppMark() {
  return (
    <svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 2a10 10 0 0 0-8.6 15.1L2 22l5-1.3A10 10 0 1 0 12 2Zm0 18.2a8.2 8.2 0 0 1-4.2-1.2l-.3-.2-3 .8.8-2.9-.2-.3A8.2 8.2 0 1 1 12 20.2Zm4.5-6.1c-.2-.1-1.5-.7-1.7-.8-.2-.1-.4-.1-.6.1l-.8 1c-.1.2-.3.2-.5.1a6.7 6.7 0 0 1-3.3-2.9c-.3-.4.3-.4.7-1.4.1-.2 0-.3 0-.4l-.8-1.8c-.2-.5-.4-.4-.6-.4h-.5a1 1 0 0 0-.7.3 3 3 0 0 0-.9 2.2 5.2 5.2 0 0 0 1.1 2.8 11.9 11.9 0 0 0 4.6 4c1.7.7 2.3.8 3.2.7a2.7 2.7 0 0 0 1.8-1.3 2.2 2.2 0 0 0 .2-1.3c-.1-.1-.3-.2-.5-.3Z" />
    </svg>
  );
}

function Tick({ accent }) {
  return (
    <span
      aria-hidden="true"
      className="grid h-7 w-7 flex-none place-items-center rounded-full"
      style={{ color: accent, backgroundColor: `color-mix(in srgb, ${accent} 8%, white)` }}
    >
      <svg width="14" height="14" viewBox="0 0 20 20" fill="currentColor">
        <path d="M7.7 13.3 4.4 10l-1.2 1.2 4.5 4.5 9.1-9.1-1.2-1.2z" />
      </svg>
    </span>
  );
}

// `sent`: what this card showed when the form went through, kept on the message
// so a page refresh shows it as sent instead of offering the form again.
function HandoverFormCard({ form, sent = null, whatsappAvailable = false, accent, onSubmit }) {
  const t = useCopy();
  const id = useId();
  const [subject, setSubject] = useState(handoverSubjectText(form?.subject));
  const [editingSubject, setEditingSubject] = useState(false);
  const [contact, setContact] = useState(form?.whatsapp || form?.email || '');
  const [name, setName] = useState('');
  const [problem, setProblem] = useState('');
  const [sending, setSending] = useState(null);
  const [done, setDone] = useState(sent);
  const subjectValue = handoverSubjectText(subject);
  const sentSubject = handoverSubjectText(done?.subject);
  const countryCode = form?.whatsapp_country_code;
  const typed = contact.trim();
  const readsWithCountryCode =
    Boolean(countryCode) &&
    Boolean(typed) &&
    contactKind(typed) === 'whatsapp' &&
    !typed.startsWith('+');

  // The tenant's own colour, as the chat's person accent uses it (ClientContext).
  const accentColor = accent;
  const cardStyle = {
    boxShadow: '0 2px 12px -2px rgba(0,0,0,0.07)',
    '--hf-accent': accentColor,
    '--hf-ring': `color-mix(in srgb, ${accentColor} 35%, transparent)`,
  };
  const cardClass =
    'w-full rounded-2xl rounded-tl-sm border border-stone-200 bg-white px-4 pt-4 pb-3.5';
  const inputClass =
    'w-full rounded-[10px] border border-stone-300 bg-stone-50 px-3 text-slate-900 ' +
    'placeholder:text-slate-400 focus:border-[var(--hf-accent)] focus:bg-white ' +
    'focus:shadow-[0_0_0_3px_var(--hf-ring)] focus:outline-none';
  const focusRing = 'focus-visible:outline-none focus-visible:shadow-[0_0_0_3px_var(--hf-ring)]';

  const send = async (choice) => {
    if (sending) return;
    const fields = { choice, name: name.trim() || null, subject: subjectValue || null };
    if (choice === 'contact') {
      const value = contact.trim();
      if (!value) {
        setProblem('contact_required');
        return;
      }
      const bad = contactProblem(value);
      if (bad) {
        setProblem(bad);
        return;
      }
      Object.assign(fields, contactFields(value));
    }
    setProblem('');
    setSending(choice);
    const outcome =
      choice === 'contact'
        ? {
            choice,
            kind: contactKind(contact.trim()),
            value: contact.trim(),
            subject: subjectValue,
          }
        : { choice };
    const result = await onSubmit(fields, outcome);
    setSending(null);
    if (result?.error) {
      setProblem(result.error);
      return;
    }
    setDone(result.sent);
  };

  if (done?.choice === 'continue_on_whatsapp') {
    // The reviewed invitation and its link arrive as the next message.
    return null;
  }
  if (done) {
    return (
      <div className={cardClass} style={cardStyle} role="status">
        <div className="flex items-start gap-3">
          <Tick accent={accentColor} />
          <div className="text-sm leading-relaxed">
            {done.choice === 'contact' ? (
              <p className="text-slate-900">
                {t('Done! We will reply by')} {done.kind === 'email' ? t('e-mail') : 'WhatsApp'}:{' '}
                <strong className="font-semibold">{done.value}</strong>
              </p>
            ) : (
              <p className="text-slate-900">
                {t('All right, the team will reply here in this chat.')}
              </p>
            )}
            {done.choice === 'contact' && sentSubject ? (
              <p className="mt-0.5 text-[13px] text-slate-500">
                {t('Subject')}: {sentSubject}
              </p>
            ) : null}
            {done.choice === 'wait' ? (
              <p className="mt-0.5 text-[13px] text-slate-500">
                {t('Keep this page open to see the reply.')}
              </p>
            ) : null}
          </div>
        </div>
      </div>
    );
  }

  const sendingLabel = (
    <>
      <span
        aria-hidden="true"
        className="h-4 w-4 animate-spin rounded-full border-2 border-current border-r-transparent motion-reduce:animate-none"
      />
      {t('Sending...')}
    </>
  );
  const subjectId = `${id}-subject`;
  const contactId = `${id}-contact`;
  const hintId = `${id}-hint`;
  const nameId = `${id}-name`;
  const leadId = `${id}-lead`;

  return (
    <form
      className={cardClass}
      style={cardStyle}
      noValidate
      aria-labelledby={leadId}
      onSubmit={(event) => {
        event.preventDefault();
        send('contact');
      }}
      data-testid="handover-form"
    >
      <p id={leadId} className="mb-0.5 text-sm font-medium leading-relaxed text-slate-900">
        {t('So the team can get back to you, leave a contact:')}
      </p>

      {editingSubject ? (
        <div className="mb-2 mt-1">
          <label className="sr-only" htmlFor={subjectId}>
            {t('Subject')}
          </label>
          <input
            id={subjectId}
            className={`${inputClass} min-h-[40px] text-[13.5px]`}
            value={subject}
            maxLength={200}
            placeholder={t('What is it about?')}
            autoFocus
            onChange={(event) => setSubject(event.target.value)}
            onBlur={() => setEditingSubject(false)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                setEditingSubject(false);
              }
            }}
          />
        </div>
      ) : (
        <p className="mb-3.5 text-[13px] leading-relaxed text-slate-500">
          {subjectValue ? (
            <>
              {t('About:')} <span className="text-slate-900">{subjectValue}</span>
              <span className="whitespace-nowrap">
                {' · '}
                <button
                  type="button"
                  className={`cursor-pointer rounded underline underline-offset-[3px] hover:text-slate-900 ${focusRing}`}
                  onClick={() => setEditingSubject(true)}
                >
                  {t('edit')}
                </button>
              </span>
            </>
          ) : (
            <button
              type="button"
              className={`cursor-pointer rounded underline underline-offset-[3px] hover:text-slate-900 ${focusRing}`}
              onClick={() => setEditingSubject(true)}
            >
              {t('Add a subject')}
            </button>
          )}
        </p>
      )}

      {whatsappAvailable ? (
        <>
          <button
            type="button"
            className={`flex min-h-[48px] w-full cursor-pointer items-center justify-center gap-2 rounded-xl font-semibold text-white hover:brightness-105 ${focusRing}`}
            style={{ backgroundColor: WHATSAPP_GREEN }}
            aria-busy={sending === 'continue_on_whatsapp'}
            onClick={() => send('continue_on_whatsapp')}
          >
            {sending === 'continue_on_whatsapp' ? (
              sendingLabel
            ) : (
              <>
                <WhatsAppMark />
                {t('Continue on WhatsApp')}
              </>
            )}
          </button>
          <div
            aria-hidden="true"
            className="my-3.5 flex items-center gap-3 text-xs text-slate-500 before:h-px before:flex-1 before:bg-stone-200 after:h-px after:flex-1 after:bg-stone-200"
          >
            {t('or leave a contact')}
          </div>
        </>
      ) : null}

      <div className="mb-2">
        <label className="sr-only" htmlFor={contactId}>
          {t('WhatsApp or e-mail')}
        </label>
        <input
          id={contactId}
          className={`${inputClass} min-h-[44px] text-sm`}
          type="text"
          inputMode="email"
          autoComplete="on"
          value={contact}
          placeholder={t('WhatsApp or e-mail')}
          aria-invalid={problem ? 'true' : 'false'}
          aria-describedby={hintId}
          onChange={(event) => {
            setContact(event.target.value);
            if (problem && !contactProblem(event.target.value)) setProblem('');
          }}
          onBlur={() => setProblem(contactProblem(contact))}
        />
        <p
          id={hintId}
          aria-live="polite"
          className="mt-1.5 px-0.5 text-xs text-amber-700 empty:hidden"
        >
          {problemText(t, problem)}
        </p>
        {readsWithCountryCode && !problem ? (
          <p className="mt-1.5 px-0.5 text-xs text-slate-500" data-testid="country-code-hint">
            {t('We will use')} +{countryCode}. {t('Another country? Start with + and the code.')}
          </p>
        ) : null}
      </div>
      <div className="mb-2">
        <label className="sr-only" htmlFor={nameId}>
          {t('Your name (optional)')}
        </label>
        <input
          id={nameId}
          className={`${inputClass} min-h-[40px] text-[13.5px]`}
          autoComplete="name"
          maxLength={80}
          value={name}
          placeholder={t('Your name (optional)')}
          onChange={(event) => setName(event.target.value)}
        />
      </div>

      <button
        type="submit"
        className={`mt-1.5 flex min-h-[48px] w-full cursor-pointer items-center justify-center gap-2 rounded-xl font-semibold ${
          whatsappAvailable
            ? 'border border-stone-300 bg-transparent text-slate-900 hover:bg-stone-50'
            : 'text-white hover:brightness-105'
        } ${focusRing}`}
        style={whatsappAvailable ? undefined : { backgroundColor: accentColor }}
        aria-busy={sending === 'contact'}
      >
        {sending === 'contact' ? sendingLabel : t('Call the team')}
      </button>
      <button
        type="button"
        className={`mx-auto mt-1.5 block cursor-pointer rounded px-1 py-1.5 text-[13px] text-slate-500 underline underline-offset-[3px] hover:text-slate-900 ${focusRing}`}
        aria-busy={sending === 'wait'}
        onClick={() => send('wait')}
      >
        {sending === 'wait' ? t('Sending...') : t("I'd rather wait here without leaving a contact")}
      </button>
      <p className="mt-1 text-center text-[11.5px] leading-normal text-slate-400">
        {t('We will use your contact only to reply to this request.')}
      </p>
    </form>
  );
}

export default memo(HandoverFormCard);
